import { encodeFunctionData, parseUnits } from "viem";
import { ethers } from "ethers";
import { addToast } from "@heroui/react";
import {
  updateCollectionBuy,
  writeContractApi
} from "../api/methods-marketplace";
import tradeABI from "../../src/abis/trade.json";
import { rainbowKitConfig } from "../utils/config";
import { writeContract, waitForTransactionReceipt } from "@wagmi/core";

export const buyAsset = async (buyData) => {
  const {
    tradeContractAddress,
    address,
    collectionId,
    erc20BAFAddress,
    buyQuantity,
    item,
    nftAddress,
    setBuyStatus,
    buyerPlatformFee,
    refreshBalance,
    fetchNftData,
    publicClient,
    isCustodialWallet
  } = buyData;

  try {
    setBuyStatus("process");

    const owner_address = item?.data?.collection?.owner?.address;
    const tokenId = item?.data?.collection?.token;
    const nonce = item?.data?.collection?.nonce;
    const sellerSign = item?.data?.collection?.sign_instant_sale_price;

    const buyPrice = item?.data?.collection?.resale_price
      ? item?.data?.collection?.resale_price
      : item?.data?.collection?.instant_sale_price;

    // Convert price to Wei
    const unitPrice = parseUnits(buyPrice?.toString(), 18);
    const buyerFee = (buyPrice * buyerPlatformFee) / 100;
    const formattedBuyerFee = parseUnits(buyerFee.toString(), 18);

    const totalpaymentAmt = unitPrice + formattedBuyerFee;

    const order = {
      seller: owner_address,
      buyer: address,
      erc20Address: erc20BAFAddress,
      nftAddress: nftAddress,
      unitPrice: unitPrice.toString(),
      skipRoyalty: true,
      amount: totalpaymentAmt.toString(),
      tokenId: tokenId,
      royaltyFee: 0,
      nonce: nonce
    };

    const { v, r, s } = ethers.Signature.from(sellerSign);
    const vrs_n = [v, r, s, order.nonce];

    let transactionHash = null;

    if (isCustodialWallet) {
      console.log("Custodial wallet purchase initiated");

      if (!publicClient) {
        throw new Error(
          "publicClient is required for custodial wallet purchases"
        );
      }

      const calldata = encodeFunctionData({
        abi: tradeABI,
        functionName: "buyAsset",
        args: [order, vrs_n]
      });

      console.log("Encoded calldata:", calldata);

      const apiRes = await writeContractApi({
        address: tradeContractAddress,
        encoded_data: calldata
      });

      if (!apiRes?.data?.tx_hash) {
        throw new Error("No transaction hash received from custodial wallet");
      }

      transactionHash = apiRes.data.tx_hash;

      const receipt = await publicClient.waitForTransactionReceipt({
        hash: transactionHash
      });

      if (receipt.status !== "success") {
        throw new Error("Purchase transaction failed on blockchain");
      }

      const updateResult = await updateCollectionBuy({
        collectionId,
        quantity: buyQuantity,
        transactionHash
      });

      if (updateResult?.status === 200 || updateResult?.success) {
        setBuyStatus("done");
        addToast({
          title: "Purchase Successful",
          description: "NFT purchased and collection updated successfully!",
          color: "success",
          variant: "bordered"
        });
      } else {
        setBuyStatus("update-failed");
        addToast({
          title: "Purchase Successful, Update Failed",
          description: "NFT purchased but failed to update collection.",
          color: "warning",
          variant: "bordered"
        });
      }

      await refreshBalance?.();
      await fetchNftData?.();
    } else {
      const txHash = await writeContract(rainbowKitConfig, {
        abi: tradeABI,
        address: tradeContractAddress,
        functionName: "buyAsset",
        args: [order, vrs_n]
      });

      console.log("Transaction hash:", txHash);

      const receipt = await waitForTransactionReceipt(rainbowKitConfig, {
        hash: txHash,
        confirmations: 1
      });

      if (receipt.status !== "success") {
        addToast({
          title: "BuyAsset Failed",
          description: "Oops... Failed to Purchase NFT"
        });
      }

      transactionHash = receipt?.transactionHash || txHash;

      const updateResult = await updateCollectionBuy({
        collectionId,
        quantity: buyQuantity,
        transactionHash: transactionHash
      });

      if (updateResult?.status === 200 || updateResult?.success) {
        setBuyStatus("done");
        addToast({
          title: "Purchase Successful",
          description: "NFT purchased and collection updated successfully!",
          color: "success",
          variant: "bordered"
        });
      } else {
        setBuyStatus("update-failed");
        addToast({
          title: "Purchase Successful, Update Failed",
          description: "NFT purchased but failed to update collection.",
          color: "warning",
          variant: "bordered"
        });
      }
    }

    return {
      success: true,
      transactionHash: transactionHash
    };
  } catch (err) {
    console.error("❌ Purchase error:", err);

    setBuyStatus("failed");
    let errorMessage = "";

    if (
      err?.message?.includes("User rejected") ||
      err?.message?.includes("User denied") ||
      err?.shortMessage?.includes("User denied")
    ) {
      errorMessage = "User rejected the request";
    } else if (err?.message?.includes("seller sign verification failed")) {
      errorMessage = "Seller sign verification failed";
    } else if (
      err?.reason?.includes("ERC721") ||
      err?.message?.includes("ERC721")
    ) {
      errorMessage = "You are not the token owner or approved";
    } else {
      errorMessage =
        err?.response?.data?.message ||
        err?.reason ||
        err?.message ||
        "Failed to complete the purchase.";
    }

    addToast({
      title: "Purchase Failed",
      description: errorMessage,
      color: "danger",
      variant: "bordered"
    });

    return { success: false, error: errorMessage };
  }
};
